import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { gbp } from "../lib/format";
import { TrendingUp, LineChart as LineChartIcon } from "lucide-react";

const monthLabel = (d) =>
  d.toLocaleString("en-GB", { month: "short" }).toUpperCase();

const buildTimeline = (investments) => {
  const months = [];
  const now = new Date();
  now.setDate(1);
  for (let i = 5; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d);
  }

  return months.map((d) => {
    const endOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    let payout = 0;
    let holdings = 0;
    (investments || []).forEach((inv) => {
      const investedAt = new Date(inv.invested_at);
      if (investedAt < endOfMonth) {
        payout += (inv.amount_gbp * inv.yield_pct) / 100 / 12;
        holdings += inv.amount_gbp;
      }
    });
    return {
      month: monthLabel(d),
      payout: Math.round(payout * 100) / 100,
      holdings: Math.round(holdings),
    };
  });
};

const TooltipCard = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs shadow-lg">
      <div className="overline" style={{ color: "#94a3b8" }}>{label}</div>
      <div className="mt-1 flex items-center gap-2 font-mono-num text-white">
        <span className="h-2 w-2 rounded-sm" style={{ background: "#D4AF37" }} />
        Payout <span className="ml-auto font-semibold">{gbp(row.payout, { decimals: 2 })}</span>
      </div>
      <div className="mt-1 font-mono-num text-slate-400">
        Held: {gbp(row.holdings)}
      </div>
    </div>
  );
};

export const YieldTimelineChart = ({ investments }) => {
  const data = useMemo(() => buildTimeline(investments), [investments]);
  const total6mo = data.reduce((s, r) => s + r.payout, 0);
  const latest = data[data.length - 1]?.payout || 0;

  const hasData = data.some((d) => d.payout > 0);

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-6"
      data-testid="yield-timeline-chart"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2"><LineChartIcon className="h-3 w-3 brickstok-gold" /> Distributions</div>
          <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Monthly Yield Accrual
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Stablecoin payouts across the last six months.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="overline">6-Month Total</div>
            <div className="mt-1 font-display text-xl font-semibold text-slate-900 font-mono-num" data-testid="chart-6mo-total">
              {gbp(total6mo, { decimals: 2 })}
            </div>
          </div>
          <div>
            <div className="overline">This Month</div>
            <div className="mt-1 flex items-center gap-1 font-display text-xl font-semibold font-mono-num" style={{ color: "#D4AF37" }}>
              <TrendingUp className="h-4 w-4" /> {gbp(latest, { decimals: 2 })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 h-64 w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="month"
                stroke="#94a3b8"
                tick={{ fontSize: 11, fontFamily: "Manrope", letterSpacing: 2 }}
                axisLine={{ stroke: "#E2E8F0" }}
                tickLine={false}
              />
              <YAxis
                stroke="#94a3b8"
                tick={{ fontSize: 11, fontFamily: "Manrope" }}
                axisLine={{ stroke: "#E2E8F0" }}
                tickLine={false}
                tickFormatter={(v) => `£${Math.round(v)}`}
                width={60}
              />
              <Tooltip content={<TooltipCard />} cursor={{ stroke: "#D4AF37", strokeDasharray: "3 3" }} />
              <Area
                type="monotone"
                dataKey="payout"
                stroke="#D4AF37"
                strokeWidth={2.5}
                fill="url(#goldFill)"
                activeDot={{ r: 5, fill: "#D4AF37", stroke: "#0F172A", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full flex-col items-center justify-center rounded-md border border-dashed border-slate-200 text-center">
            <div className="overline">No distributions yet</div>
            <p className="mt-2 max-w-sm text-sm text-slate-500">
              Once you allocate capital, monthly stablecoin payouts will appear here as they accrue.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default YieldTimelineChart;
